import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { cartoesApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { formatMoney } from '../../lib/utils'
import type { FaturaTransacao } from '../../types'

const DICA_PAGA = 'Reabra a fatura para editar ajustes'

/** Novo total da fatura se o ajuste for excluído (o ajuste reduz o total, então tirar soma de volta). */
function totalAposExcluirAjuste(total: number, valorAjuste: number) {
  return Math.round((total + valorAjuste) * 100) / 100
}

/** Novo total da fatura se o ajuste passar de `antigo` para `novo`. */
function totalAposEditarAjuste(total: number, antigo: number, novo: number) {
  return Math.round((total + antigo - novo) * 100) / 100
}

/** Editar / excluir um ajuste de fatura (estorno, pagamento recebido). Só em fatura não paga;
 * na paga os botões ficam desabilitados com a dica para reabrir a fatura. */
export function AjusteFaturaAcoes({
  ajuste, faturaPaga, totalFatura,
}: { ajuste: FaturaTransacao; faturaPaga: boolean; totalFatura: number }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [editando, setEditando] = useState(false)
  const [excluindo, setExcluindo] = useState(false)
  const [form, setForm] = useState({ descricao: '', valor: '', data: '' })
  const [erro, setErro] = useState<string | null>(null)

  const invalidar = () => {
    for (const k of ['fatura', 'fatura-categorias', 'faturas', 'faturas-resumo', 'cartoes', 'transacoes', 'contas', 'dashboard']) {
      qc.invalidateQueries({ queryKey: [k] })
    }
  }

  const salvar = useMutation({
    mutationFn: () => cartoesApi.atualizarAjuste(ajuste.id, { valor: Number(form.valor), data: form.data, descricao: form.descricao.trim() }),
    onSuccess: () => { invalidar(); setEditando(false); toast.success('Ajuste atualizado.') },
    onError: (e: Error) => setErro(e.message),
  })
  const excluir = useMutation({
    mutationFn: () => cartoesApi.removerAjuste(ajuste.id),
    onSuccess: () => { invalidar(); setExcluindo(false); toast.success('Ajuste excluído.') },
    onError: (e: Error) => { setExcluindo(false); toast.error(e.message) },
  })

  function abrirEdicao() {
    setErro(null)
    setForm({ descricao: ajuste.description, valor: String(Math.abs(ajuste.amount)), data: ajuste.date.slice(0, 10) })
    setEditando(true)
  }

  function confirmar() {
    const valor = Number(form.valor)
    if (!form.descricao.trim()) return setErro('Informe a descrição.')
    if (!(valor > 0)) return setErro('O valor deve ser maior que zero.')
    if (!form.data) return setErro('Informe a data.')
    setErro(null)
    salvar.mutate()
  }

  const novoValor = Number(form.valor)
  const novoTotal = totalAposEditarAjuste(totalFatura, ajuste.amount, novoValor > 0 ? novoValor : ajuste.amount)

  if (faturaPaga) {
    return (
      <div className="flex justify-end gap-1" title={DICA_PAGA}>
        <IconButton icon="ri-pencil-fill" label={`${DICA_PAGA} (${ajuste.description})`} tone="muted" disabled />
        <IconButton icon="ri-delete-bin-5-fill" label={`${DICA_PAGA} (${ajuste.description})`} tone="muted" disabled />
      </div>
    )
  }

  return (
    <>
      <div className="flex justify-end gap-1">
        <IconButton icon="ri-pencil-fill" label={`Editar ajuste ${ajuste.description}`} tone="info" onClick={abrirEdicao} />
        <IconButton icon="ri-delete-bin-5-fill" label={`Excluir ajuste ${ajuste.description}`} tone="danger" onClick={() => setExcluindo(true)} />
      </div>

      <Modal
        open={editando}
        onClose={() => setEditando(false)}
        title="Editar ajuste de fatura"
        footer={
          <>
            <Button variant="light" onClick={() => setEditando(false)}>Cancelar</Button>
            <Button icon="ri-save-line" loading={salvar.isPending} onClick={confirmar}>Salvar alterações</Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Descrição" className="sm:col-span-2" value={form.descricao} onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))} />
          <Input label="Valor (R$)" type="number" min={0} step="0.01" value={form.valor} onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))} />
          <Input label="Data" type="date" value={form.data} onChange={(e) => setForm((f) => ({ ...f, data: e.target.value }))} />
          <p className="text-13 text-muted sm:col-span-2">
            Total da fatura: de <strong className="text-white">{formatMoney(totalFatura)}</strong> para <strong className="text-white">{formatMoney(novoTotal)}</strong>.
          </p>
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense sm:col-span-2" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <ConfirmDialog
        open={excluindo}
        onClose={() => setExcluindo(false)}
        onConfirm={() => excluir.mutate()}
        loading={excluir.isPending}
        title="Excluir ajuste?"
        message={<>
          O ajuste <strong className="text-white">{ajuste.description}</strong> de {formatMoney(ajuste.amount)} será excluído.
          O total da fatura vai de <strong className="text-white">{formatMoney(totalFatura)}</strong> para{' '}
          <strong className="text-white">{formatMoney(totalAposExcluirAjuste(totalFatura, ajuste.amount))}</strong>.
        </>}
      />
    </>
  )
}
